import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface PendingDisclosure {
  id: number;
  title: string;
  content: string;
  version: string | null;
}

interface DisclosureGateProps {
  onComplete: () => void;
}

const DisclosureGate: React.FC<DisclosureGateProps> = ({ onComplete }) => {
  const { logout } = useAuth();
  const [disclosures, setDisclosures] = useState<PendingDisclosure[]>([]);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPending = async () => {
      try {
        const res = await api.get('/api/v1/disclosures/active');
        const pending = res.data.filter((d: any) => d.needs_acknowledgment);
        setDisclosures(pending);
        if (pending.length === 0) {
          // Nothing pending — lift gate immediately
          onComplete();
        }
      } catch {
        setError('Failed to load disclosures. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };
    fetchPending();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleChecked = (id: number) => {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const allChecked = disclosures.length > 0 && disclosures.every(d => checked.has(d.id));

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      for (const d of disclosures) {
        await api.post(`/api/v1/disclosures/${d.id}/acknowledge`);
      }
      onComplete();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save acknowledgment. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        role="status"
        aria-label="Loading disclosures"
      >
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #FDF6EC 0%, #F5E6D0 50%, #FDF6EC 100%)',
        p: { xs: 2, sm: 3 },
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: { xs: 3, sm: 5 },
          maxWidth: 600,
          width: '100%',
          borderRadius: 3,
          border: '1px solid rgba(58, 140, 140, 0.2)',
        }}
        component="main"
        aria-labelledby="disclosure-gate-heading"
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <GavelIcon sx={{ fontSize: 64, color: '#3A8C8C', mb: 2 }} aria-hidden="true" />
          <Typography
            id="disclosure-gate-heading"
            variant="h5"
            sx={{ fontWeight: 700, color: '#3A8C8C', mb: 1 }}
          >
            Review Required Disclosures
          </Typography>
          <Typography variant="body2" sx={{ color: '#7A5F4E' }}>
            Before continuing, please read and acknowledge the following platform disclosures.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} role="alert">
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }} role="list" aria-label="Platform disclosures">
          {disclosures.map(d => (
            <Accordion
              key={d.id}
              role="listitem"
              sx={{ mb: 1, border: '1px solid rgba(58,140,140,0.15)', borderRadius: 1, '&:before': { display: 'none' } }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`disclosure-${d.id}-content`}
                id={`disclosure-${d.id}-header`}
              >
                <Typography sx={{ fontWeight: 600, color: '#3A3A3A' }}>
                  {d.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails id={`disclosure-${d.id}-content`}>
                <Typography variant="body2" sx={{ color: '#4A3728', mb: 2, lineHeight: 1.7 }}>
                  {d.content}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checked.has(d.id)}
                      onChange={() => toggleChecked(d.id)}
                      color="primary"
                      inputProps={{ 'aria-label': `Acknowledge: ${d.title}` }}
                    />
                  }
                  label="I have read and acknowledge this disclosure"
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: '#3A3A3A' } }}
                />
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!allChecked || submitting}
            aria-busy={submitting}
            sx={{ py: 1.5, fontWeight: 600, minHeight: 44 }}
          >
            {submitting ? <CircularProgress size={22} color="inherit" aria-label="Saving acknowledgments" /> : 'Continue to Quantato'}
          </Button>
          <Button
            variant="text"
            onClick={logout}
            sx={{ color: '#999', textTransform: 'none', minHeight: 44 }}
          >
            Sign out
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default DisclosureGate;
